import React from 'react'

import { fetchApi } from '@hozana/api/fetchApi'
import { useDispatch } from '@hozana/redux/hooks'
import { QA } from '@hozana/tests/constants'

import type { TButtonColor } from 'elements/button/BaseButton'
import { Button } from 'elements/button/Button'
import { Div, TDivProps } from 'elements/layout/Div'
import { Alert } from 'elements/ui/Alert'

import { ICON } from 'config/icons'

import { COMMUNITY_DISPLAYMODE } from 'modules/community/constants'
import { editCommunityMutation } from 'modules/community/queries'
import type { TCommunity } from 'modules/community/types'

type TDisplayMode = {
  name: COMMUNITY_DISPLAYMODE
  icon: ICON
  text: string
  styles: Partial<TButtonColor>
}

const displayModes: TDisplayMode[] = [
  {
    name: COMMUNITY_DISPLAYMODE.DISPLAYED,
    icon: ICON.CHECK,
    text: 'trans:community:community.display-mode.displayed',
    styles: {
      hoverBg: 'darkGreen',
      bg: 'lightGreen',
    },
  },
  {
    name: COMMUNITY_DISPLAYMODE.TO_REWORK,
    icon: ICON.CHECK,
    text: 'trans:community:community.display-mode.to_rework',
    styles: {
      hoverBg: 'darkBlue',
      bg: 'lightBlue',
    },
  },
  {
    name: COMMUNITY_DISPLAYMODE.FAVORITE,
    icon: ICON.HEART,
    text: 'trans:community:community.display-mode.favorite',
    styles: {
      hoverBg: 'darkPurple',
      bg: 'lightPurple',
    },
  },
  {
    name: COMMUNITY_DISPLAYMODE.HIDDEN,
    icon: ICON.CLOSE,
    text: 'trans:community:community.display-mode.hidden',
    styles: {
      hoverBg: 'darkRed',
      bg: 'lightRed',
    },
  },
  {
    name: COMMUNITY_DISPLAYMODE.UNTREATED,
    icon: ICON.CALENDAR,
    text: 'trans:community:community.display-mode.untreated',
    styles: {
      hoverBg: 'orange',
      bg: 'whiteOrange',
    },
  },
]

export type TDisplayModeSelectorProps = {
  community: TCommunity
} & TDivProps

export const DisplayModeSelector: TFC<TDisplayModeSelectorProps> = ({ community }) => {
  const dispatch = useDispatch()

  const onSelect = (displayMode: COMMUNITY_DISPLAYMODE) => () =>
    dispatch(fetchApi(editCommunityMutation({ communityId: community.id, displayMode })))

  return (
    <>
      <Div>
        {displayModes.map((displayMode) => {
          const isSelected = displayMode.name === community.displayMode
          return (
            <Button
              size="tiny"
              bold={isSelected}
              icon={displayMode.icon}
              m="0 0 5px"
              onClick={onSelect(displayMode.name)}
              key={displayMode.name}
              data-testid={QA.COMMUNITY_PAGE.SUPER_ADMIN_ACTIONS.DISPLAY_MODE_BUTTON}
              // styles
              color={isSelected ? 'white' : displayMode.styles.hoverBg}
              borderColor={isSelected ? displayMode.styles.hoverBg : displayMode.styles.bg}
              bg={isSelected ? displayMode.styles.hoverBg : displayMode.styles.bg}
              hoverBg={displayMode.styles.hoverBg}
              hoverColor="white"
              hoverBorderColor={displayMode.styles.hoverBg}
            >
              {displayMode.text}
            </Button>
          )
        })}
      </Div>
      {!community.displayMode && (
        <Alert m="0 10px 0 0" status="important">
          trans:community:community.display-mode.label
        </Alert>
      )}
    </>
  )
}
